import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../lib/api'

function formatMonthLabel(monthStr) {
	const [year, month] = String(monthStr || '').split('-').map(Number)
	if (!year || !month) return 'Month'
	const date = new Date(Date.UTC(year, month - 1, 1))
	return date.toLocaleString([], { month: 'long', year: 'numeric' })
}

const WEEK_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export default function StreakPage({ currentUser, onLogin, onStreakCountChange }) {
	const [loading, setLoading] = useState(false)
	const [month, setMonth] = useState('')
	const [data, setData] = useState(null)

	async function loadStreak(targetMonth) {
		if (!currentUser?.id) return
		setLoading(true)
		try {
			const monthQuery = targetMonth ? `?month=${encodeURIComponent(targetMonth)}` : ''
			const res = await apiFetch(`/streak/me${monthQuery}`)
			if (!res.ok) throw new Error('Failed to load streak')
			const payload = await res.json()
			setData(payload)
			setMonth(payload.month)
			onStreakCountChange?.(Number(payload?.summary?.currentStreak || 0))
		} catch {
			setData(null)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		loadStreak()
	}, [currentUser?.id])

	const firstWeekdayIndex = useMemo(() => {
		if (!month) return 0
		const [year, monthNum] = month.split('-').map(Number)
		return new Date(Date.UTC(year, monthNum - 1, 1)).getUTCDay()
	}, [month])

	const calendarCells = useMemo(() => {
		const days = data?.calendar || []
		const leading = Array.from({ length: firstWeekdayIndex }, (_, idx) => ({
			key: `empty-${idx}`,
			empty: true,
		}))
		return [
			...leading,
			...days.map((entry) => ({
				key: entry.date,
				...entry,
			})),
		]
	}, [data?.calendar, firstWeekdayIndex])

	if (!currentUser) {
		return (
			<div className="rounded-3xl border border-gray-200 bg-white p-5 text-sm text-gray-600">
				Please login to view your streak.
				<div className="mt-3">
					<button onClick={onLogin} className="rounded-xl bg-orange-500 px-4 py-2 text-xs font-semibold text-white">Login</button>
				</div>
			</div>
		)
	}

	const summary = data?.summary || { currentStreak: 0, longestStreak: 0, totalActiveDays: 0, activeDaysInMonth: 0 }
	const rewards = data?.rewards || { tiers: [], nextReward: null }

	return (
		<section className="mx-auto w-full max-w-5xl pb-6">
			<div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 p-6 text-white shadow-2xl">
				<div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/20 blur-2xl" />
				<div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-yellow-200/20 blur-2xl" />
				<p className="text-xs uppercase tracking-[0.28em] text-orange-100">Posting Momentum</p>
				<div className="mt-3 flex flex-wrap items-end gap-3">
					<span className="text-5xl font-extrabold leading-none">{summary.currentStreak}</span>
					<span className="mb-1 rounded-full bg-white/20 px-3 py-1 text-sm font-semibold text-white">day streak</span>
				</div>
				<div className="mt-5 grid gap-3 sm:grid-cols-3">
					<div className="rounded-2xl border border-white/30 bg-white/15 p-3">
						<p className="text-xs text-orange-100">Longest streak</p>
						<p className="mt-1 text-xl font-bold">{summary.longestStreak} days</p>
					</div>
					<div className="rounded-2xl border border-white/30 bg-white/15 p-3">
						<p className="text-xs text-orange-100">Active days</p>
						<p className="mt-1 text-xl font-bold">{summary.totalActiveDays}</p>
					</div>
					<div className="rounded-2xl border border-white/30 bg-white/15 p-3">
						<p className="text-xs text-orange-100">This month</p>
						<p className="mt-1 text-xl font-bold">{summary.activeDaysInMonth} days</p>
					</div>
				</div>
			</div>

			<div className="mt-5 rounded-3xl border border-gray-200 bg-white/90 p-5 shadow-lg">
				<div className="mb-4 flex items-center justify-between">
					<h3 className="text-base font-bold text-gray-900">Rewards</h3>
					{rewards.nextReward ? (
						<span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
							{Math.max(0, rewards.nextReward.days - summary.currentStreak)} days to {rewards.nextReward.title}
						</span>
					) : (
						<span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">All rewards unlocked</span>
					)}
				</div>

				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
					{rewards.tiers.map((tier) => (
						<div
							key={tier.days}
							className={`rounded-2xl border p-3 ${tier.unlocked ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}
						>
							<p className="text-sm font-bold text-gray-900">{tier.days}-day</p>
							<p className="mt-1 text-xs font-semibold text-gray-700">{tier.title}</p>
							<p className="mt-1 text-xs text-gray-500">{tier.reward}</p>
							<p className={`mt-2 text-[11px] font-bold ${tier.unlocked ? 'text-emerald-700' : 'text-gray-500'}`}>
								{tier.unlocked ? 'Unlocked' : 'Locked'}
							</p>
						</div>
					))}
				</div>
			</div>

			<div className="mt-5 rounded-3xl border border-gray-200 bg-white p-5 shadow-lg">
				<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
					<h3 className="text-base font-bold text-gray-900">Streak Calendar</h3>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => data?.navigation?.previousMonth && loadStreak(data.navigation.previousMonth)}
							disabled={!data?.navigation?.previousMonth || loading}
							className="rounded-xl border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
						>
							Prev
						</button>
						<span className="min-w-[170px] text-center text-sm font-bold text-gray-900">{formatMonthLabel(month)}</span>
						<button
							type="button"
							onClick={() => data?.navigation?.nextMonth && loadStreak(data.navigation.nextMonth)}
							disabled={!data?.navigation?.nextMonth || loading}
							className="rounded-xl border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
						>
							Next
						</button>
					</div>
				</div>

				{loading && <p className="text-sm text-gray-500">Loading streak calendar...</p>}

				{!loading && (
					<>
						<div className="mb-2 grid grid-cols-7 gap-2">
							{WEEK_HEADERS.map((label) => (
								<div key={label} className="text-center text-[11px] font-bold uppercase tracking-wide text-gray-400">{label}</div>
							))}
						</div>

						<div className="grid grid-cols-7 gap-2">
							{calendarCells.map((cell) => {
								if (cell.empty) {
									return <div key={cell.key} className="h-12 rounded-xl bg-transparent" />
								}

								return (
									<div
										key={cell.key}
										title={cell.active ? `${cell.posts} post(s), ${cell.stories} stor${cell.stories === 1 ? 'y' : 'ies'}` : 'No activity'}
										className={`h-12 rounded-xl border text-center text-sm font-bold flex items-center justify-center ${
											cell.active
												? 'border-orange-600 bg-orange-600 text-white shadow-md'
												: 'border-gray-200 bg-gray-50 text-gray-500'
										}`}
									>
										{cell.day}
									</div>
								)
							})}
						</div>

						<div className="mt-4 flex items-center gap-3 text-xs text-gray-600">
							<span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-orange-600" /> Activity day (post/story)</span>
							<span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-gray-200" /> Inactive day</span>
						</div>
					</>
				)}
			</div>
		</section>
	)
}
